<template>
  <div id="creator-root">
    <router-view class="relative"></router-view>
    <below-creator />
  </div>
</template>
<script>
import { ACTION_LOAD_FILTERS } from './store/action-types';
import BelowCreator from './components/BelowCreator';
import { MUTATION_TYPE_PRODUCT_IS_LOADED } from '@/store/modules/product/mutation-types';
import {
  MUTATION_COMPOSITION_SET_COMPOSITION,
  MUTATION_COMPOSITION_UPDATE_SIDE_CONTENT,
  MUTATION_COMPOSITION_UPDATE_SIDE_PRINTSTYLE
} from '@/store/modules/composition/mutation-types';
import { ACTION_COMPOSITION_PRODUCT_SWITCH_COMPOSITION } from '@/store/modules/composition/action-types';
import {
  MUTATION_FONT_LOAD_COLORS,
  MUTATION_FONT_LOAD_FONTS
} from '@/store/modules/font/mutation-types';
import { ACTION_AWAIT_REQUEST } from '@/store/action-types';
import { useColorStore } from '@/store-pinia/colors';

export default {
  components: { BelowCreator },
  mounted() {
    this.$store.dispatch(ACTION_LOAD_FILTERS);

    this.$store.subscribe((mutation, state) => {
      // Store Colors in Session storage. Required for TextElement.
      this.storeColorsInSession(mutation, state);

      // Store Fonts in Session storage. Required for TextElement.
      this.storeFontsInSession(mutation, state);

      // Store Composition in Session storage
      this.storeCompositionInSession(mutation, state);

      // Process loading Composition from Session on page reloads
      this.processLoadingCompositionFromSession(mutation, state);
    });
  },
  methods: {
    storeColorsInSession(mutation, state) {
      if (MUTATION_FONT_LOAD_COLORS === mutation.type) {
        this.$storage.set('COLORS', Object.assign({}, state.font.colors));
      }
    },
    storeFontsInSession(mutation, state) {
      if (MUTATION_FONT_LOAD_FONTS === mutation.type) {
        this.$storage.set('FONTS', Object.assign({}, state.font.fonts));
      }
    },
    storeCompositionInSession(mutation, state) {
      // Store Composition in Session storage when side content is updated or
      // the print style of the side content is update. Two mutations are subscribed to.
      if (
        MUTATION_COMPOSITION_UPDATE_SIDE_CONTENT === mutation.type ||
        MUTATION_COMPOSITION_UPDATE_SIDE_PRINTSTYLE === mutation.type
      ) {
        this.$storage.set(
          'COMPOSITION',
          Object.assign({}, state.composition.composition)
        );
      }
    },
    async processLoadingCompositionFromSession(mutation, state) {
      // Product must be loaded before loading Composition from Session
      if (
        MUTATION_TYPE_PRODUCT_IS_LOADED !== mutation.type ||
        true !== mutation.payload
      ) {
        return;
      }

      // Load Colors from Session. Required for TextElement.
      if (this.$storage.has('COLORS')) {
        this.$store.commit(
          MUTATION_FONT_LOAD_COLORS,
          this.$storage.get('COLORS')
        );
      }

      // Load Fonts from Session. Required for TextElement.
      if (this.$storage.has('FONTS')) {
        this.$store.commit(
          MUTATION_FONT_LOAD_FONTS,
          this.$storage.get('FONTS')
        );
      } else {
        await this.$store.dispatch(ACTION_AWAIT_REQUEST, async () => {
          const colorStore = useColorStore();
          await colorStore.loadFontColors();
        });
      }

      // Composition is not found in the session
      if (!this.$storage.has('COMPOSITION')) {
        return;
      }

      let printSide,
        sessionComposition = this.$storage.get('COMPOSITION');

      /**
       * If any print side is empty and has no content, simply discard the print side read from session.
       * This ensures that the empty print side retains the default Print style configured in Akeneo
       * and any user changes in the print style but without any content will not screw up on reload
       * or a product has been changed./
       */
      for (printSide of Object.keys(sessionComposition.printPlates)) {
        if (0 === sessionComposition.printPlates[printSide].content.length) {
          delete sessionComposition.printPlates[printSide];
        }
      }

      // Filtered Composition from the session has no printsides. Nothing more to do here.
      if (Object.keys(sessionComposition.printPlates).length === 0) {
        return;
      }

      /**
       * ___DE
       * Wenn eine der Druckplatten Inhalt hat, kann die Zusammensetzung eine R2U sein
       * oder aus dem Warenkorb geladen werden, oder der Benutzer hat
       * das Produkt einfach vom Ersteller geändert
       *
       * ___EN
       * If any of the print plate has content, the Composition might an R2U
       * or loaded from the basket or simply the user has changed the product from Creator
       */
      let printPlate;
      for (printPlate of Object.values(
        state.composition.composition.printPlates
      )) {
        if (printPlate.content.length > 0) {
          return;
        }
      }

      // Load Composition from Session
      this.$store.commit(
        MUTATION_COMPOSITION_SET_COMPOSITION,
        sessionComposition
      );

      /**
       * ___DE
       * Lösen Sie eine Produktwechselaktion aus, damit die Zusammensetzung angepasst wird.
       * Die Zusammensetzung aus der Sitzung kann für ein anderes Produkt gelten,
       * und das aktuelle Produkt im Ersteller unterscheidet sich,
       * da der Benutzer über die obere Navigation wechseln kann.
       *
       * ___EN
       * Trigger a product switch action so that Composition would be adjusted.
       * The Composition from the Session can be for another product and the current product
       * in the Creator is different as the user can change from the top navigation.
       */
      this.$store.dispatch(
        ACTION_COMPOSITION_PRODUCT_SWITCH_COMPOSITION,
        this.$store.state.product.info,
        {
          root: true
        }
      );
    }
  }
};
</script>
<style lang="scss" src="@/scss/main.scss"></style>
